/* eslint-disable react/no-unstable-nested-components --
 * MasterDataTable の columns に並ぶ cell 関数は component ではなくセルの render 関数
 * (index.tsx の雛形一覧と同じ理由)。
 */
import { useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'

import {
  Alert,
  Badge,
  Box,
  CardBody,
  CardHeader,
  CardRoot,
  Flex,
  HStack,
  SecondaryButton,
  TertiaryButton,
  Text,
  chakra,
} from '@enechain/ecloud-designsystem'
import { FaPlusRegular } from '@enechain/ecloud-icons'

import { AlertActions } from '~/components/common/AlertActions'
import {
  MasterDataTable,
  type MasterDataTableColumn,
} from '~/components/common/MasterDataTable'
import {
  isWorkflowAutomationAvailable,
  useWorkflowAutomationAvailability,
} from '~/hooks/useWorkflowAutomationAvailability'
import { convertAreaLabel } from '~/models/area'
import { localizeBuiltinWorkflowText } from '~/models/workflowBuiltinText'
import { workflowSectionLabelKey } from '~/models/workflowSection'
import { AutoCreationRuleModal } from '~/pages/AdminWorkflowTemplates/components/AutoCreationRuleModal'
import { useListWorkflowAutoCreationRules } from '~/pages/AdminWorkflowTemplates/hooks/useWorkflowAutoCreationRules'
import {
  type AutoCreationRuleRow,
  type AutoCreationRuleTemplate,
  autoCreationRuleNoticeLabelKey,
  buildAutoCreationRuleRows,
  dayFilterLabelKey,
  leadLabelSpec,
  targetDaysLabelKey,
  withPlanAutomationLock,
} from '~/pages/AdminWorkflowTemplates/models/autoCreationRule'
import { planAutomationLabelKey } from '~/pages/AdminWorkflowTemplates/models/planAutomation'

const K = 'page:adminWorkflowTemplates.autoCreationRule'

type ModalState =
  | { kind: 'closed' }
  | { kind: 'create' }
  | { kind: 'edit'; row: AutoCreationRuleRow }

type Props = {
  templates: ReadonlyArray<
    Omit<AutoCreationRuleTemplate, 'planAutomationLocked'>
  >
}

/** 雛形一覧の下の「自動起票ルール」セクション (AC-WFT-020 / 021)。 */
export const AutoCreationRuleSection = ({
  templates,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()
  const automationAvailability = useWorkflowAutomationAvailability()
  const automationAvailable = isWorkflowAutomationAvailable(
    automationAvailability,
  )
  // 取得の失敗はセクションの中に出す (AC-WFT-055)。 本番 QueryClient の
  // throwOnError: true (全画面エラー) をこの query だけ上書きする
  const { data, isError, refetch } = useListWorkflowAutoCreationRules({
    throwOnError: false,
  })
  const [modal, setModal] = useState<ModalState>({ kind: 'closed' })
  const templatesWithLock = useMemo(
    () =>
      withPlanAutomationLock(
        templates,
        data?.planAutomationLockedTemplateIds ?? [],
      ),
    [data, templates],
  )
  const rows = useMemo(
    () =>
      data !== undefined
        ? buildAutoCreationRuleRows(
            data.rules,
            templatesWithLock,
            data.planAutomationUnusedRuleIds,
          )
        : [],
    [data, templatesWithLock],
  )

  const dash = '-'
  const columns: Array<MasterDataTableColumn<AutoCreationRuleRow>> = [
    {
      key: 'template',
      header: t(`${K}.column.template`),
      align: 'left',
      cell: (row) => (
        <chakra.span fontWeight="medium">
          {localizeBuiltinWorkflowText(row.template.name, t)}
        </chakra.span>
      ),
    },
    {
      key: 'section',
      header: t(`${K}.column.section`),
      align: 'center',
      nowrap: true,
      cell: (row) => {
        const key = workflowSectionLabelKey(
          'adminWorkflowTemplates',
          row.template.section,
        )
        return key !== undefined ? t(key) : dash
      },
    },
    {
      key: 'area',
      header: t(`${K}.column.area`),
      align: 'center',
      nowrap: true,
      cell: (row) => convertAreaLabel(row.areaCode),
    },
    {
      key: 'targetDays',
      header: t(`${K}.column.targetDays`),
      align: 'left',
      cell: (row) => {
        const key = targetDaysLabelKey(row.template.section)
        return key !== undefined ? t(key) : dash
      },
    },
    {
      key: 'timing',
      header: t(`${K}.column.timing`),
      align: 'left',
      cell: (row) => {
        const lead = leadLabelSpec(
          row.schedule.leadValue,
          row.schedule.leadUnit,
        )
        const dayFilterKey = dayFilterLabelKey(row.schedule.dayFilter)
        return (
          <>
            <chakra.span display="block" whiteSpace="nowrap">
              {`${lead !== undefined ? t(lead.key, { count: lead.count }) : dash} ${row.schedule.fireTimeHm}`}
            </chakra.span>
            <chakra.span display="block" color="Text.midEmphasis" fontSize="sm">
              {dayFilterKey !== undefined ? t(dayFilterKey) : dash}
            </chakra.span>
          </>
        )
      },
    },
    {
      key: 'planAutomation',
      header: t(`${K}.column.planAutomation`),
      align: 'left',
      // 保存値 + 雛形の要承認で使われない時は理由の文 (AC-WFT-020 / 047)
      cell: (row) => (
        <>
          <chakra.span display="block" whiteSpace="nowrap">
            {t(planAutomationLabelKey(row.schedule.planAutomation))}
          </chakra.span>
          {row.planAutomationUnused && (
            <Text textStyle="caption" color="Text.warningHigh">
              {t(`${K}.planAutomation.unused`)}
            </Text>
          )}
        </>
      ),
    },
    {
      key: 'status',
      header: t(`${K}.column.status`),
      align: 'left',
      // Badge の下に、起票していない理由を caption で添える (AC-WFT-050。
      // React mock AutoCaseRuleTab の状態列)
      cell: (row) => {
        const noticeKey = autoCreationRuleNoticeLabelKey(row.notice)
        return (
          <Flex alignItems="flex-start" flexDirection="column" gap={1}>
            {row.enabled ? (
              <Badge status="positive">{t(`${K}.status.enabled`)}</Badge>
            ) : (
              <Badge status="neutral">{t(`${K}.status.disabled`)}</Badge>
            )}
            {noticeKey !== undefined && (
              <Text textStyle="caption" color="Text.warningHigh">
                {t(noticeKey)}
              </Text>
            )}
          </Flex>
        )
      },
    },
  ]

  // 公開前 (feature flag off) は出さない。取得中も出さず、off の時にカードが
  // 一瞬出て消えないようにする。取得の失敗は flag を判定できないので出す
  const showSection =
    automationAvailable && (isError || data?.featureEnabled === true)

  return (
    <>
      {showSection && (
        // 器は UI 辞書 card.md の `.card` (design case-templates.html の自動起票ルールのセクション)
        <CardRoot
          overflow="hidden"
          borderWidth="1px"
          borderColor="Border.lowEmphasis"
          borderRadius="radiiLg"
          boxShadow="shadSm"
        >
          <CardHeader
            alignItems="center"
            flexDirection="row"
            display="flex"
            padding="16px"
            borderBottomWidth="1px"
            borderBottomColor="Border.lowEmphasis"
          >
            <Text
              as="h2"
              textStyle="headerMd"
              minWidth={0}
              color="Text.highEmphasis"
            >
              {t(`${K}.title`)}
            </Text>
            <HStack flexShrink={0} marginLeft="auto">
              <SecondaryButton
                onClick={() => {
                  setModal({ kind: 'create' })
                }}
                size="sm"
              >
                <FaPlusRegular />
                {t(`${K}.action.create`)}
              </SecondaryButton>
            </HStack>
          </CardHeader>
          <CardBody
            flexDirection="column"
            gap={3}
            display="flex"
            padding="16px"
          >
            <Text color="Text.midEmphasis" fontSize="sm">
              {t(`${K}.description`)}
            </Text>
            {isError ? (
              <Alert status="error" title={t(`${K}.list.fetchFailed`)}>
                <AlertActions>
                  <TertiaryButton
                    onClick={() => {
                      void refetch()
                    }}
                    size="sm"
                  >
                    {t(`${K}.list.retry`)}
                  </TertiaryButton>
                </AlertActions>
              </Alert>
            ) : rows.length === 0 ? (
              <Box
                color="Text.midEmphasis"
                fontSize="sm"
                textAlign="center"
                paddingY="16px"
              >
                {t(`${K}.list.empty`)}
              </Box>
            ) : (
              <MasterDataTable
                columns={columns}
                getRowKey={(row) => row.id}
                onRowClick={(row) => {
                  setModal({ kind: 'edit', row })
                }}
                rowKeyboardActivation
                rows={rows}
              />
            )}
          </CardBody>
        </CardRoot>
      )}
      {modal.kind !== 'closed' && (
        <AutoCreationRuleModal
          row={modal.kind === 'edit' ? modal.row : undefined}
          isOpen={showSection}
          onClose={() => {
            setModal({ kind: 'closed' })
          }}
          onReload={() => {
            setModal({ kind: 'closed' })
            void refetch()
          }}
          templates={templatesWithLock}
        />
      )}
    </>
  )
}
